import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import { GatorMark } from '../shared/GatorMark.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { SUPPORTED_LANGUAGES, setLanguage, type SupportedLanguage } from '../../i18n.js';
import { legalPathForLanguage } from '../../lib/legalPaths.js';

const productLinks = [
  { to: '/how-it-works', key: 'landing.nav.howItWorks' },
  { to: '/features', key: 'landing.nav.features' },
  { to: '/pricing', key: 'landing.nav.pricing' },
  { to: '/for-companies', key: 'landing.nav.forCompanies' },
  { to: '/faq', key: 'landing.nav.faq' },
] as const;

const companyLinks = [
  { to: '/about', label: 'About' },
  { to: '/tutorials', label: 'Tutorials' },
  { to: '/support', label: 'Support' },
  { to: 'mailto:support@interviewgator.com', label: 'Contact' },
] as const;

export function Footer() {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language?.slice(0, 2) as SupportedLanguage) ?? 'en';

  return (
    <footer className="border-t border-ig-bg-tertiary bg-ig-bg-primary">
      <div className="mx-auto w-full max-w-[1280px] px-5 pb-6 pt-12 md:px-8 md:pb-8 md:pt-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4 md:gap-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2.5 text-ig-text-primary">
              <GatorMark className="h-8 w-8 rounded-md" alt="" />
              <span className="font-sans text-[18px] font-medium">InterviewGator</span>
            </div>
            <p className="max-w-[260px] text-[14px] leading-[1.6] text-ig-text-secondary">{t('landing.footer.tagline')}</p>
            <div className="inline-flex items-center gap-2 text-[13px]">
              {SUPPORTED_LANGUAGES.map((code, idx) => (
                <span key={code} className="inline-flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={() => setLanguage(code)}
                    variant="secondary"
                    size="sm"
                    className={
                      currentLang === code
                        ? 'h-auto border-0 bg-transparent px-0 text-[13px] text-ig-text-primary hover:bg-transparent'
                        : 'h-auto border-0 bg-transparent px-0 text-[13px] text-ig-text-secondary hover:bg-transparent hover:text-ig-text-primary'
                    }
                    aria-pressed={currentLang === code}
                  >
                    {code.toUpperCase()}
                  </Button>
                  {idx < SUPPORTED_LANGUAGES.length - 1 ? <span className="text-ig-text-tertiary">|</span> : null}
                </span>
              ))}
            </div>
          </div>

          <FooterColumn
            title={t('landing.footer.product', { defaultValue: 'Product' })}
            links={productLinks.map((item) => ({ to: item.to, label: t(item.key) }))}
          />

          <FooterColumn
            title={t('landing.footer.company', { defaultValue: 'Company' })}
            links={companyLinks.map((item) => ({
              to: item.to,
              label: (() => {
                const pl = i18n.language?.startsWith('pl');
                switch (item.label) {
                  case 'About':
                    return pl ? 'O mnie' : 'About me';
                  case 'Tutorials':
                    return pl ? 'Poradniki' : 'Tutorials';
                  case 'Support':
                    return pl ? 'Wsparcie' : 'Support';
                  default:
                    return pl ? 'Kontakt' : 'Contact';
                }
              })(),
            }))}
          />

          <FooterColumn
            title={t('landing.footer.legalHeading')}
            links={[
              { to: legalPathForLanguage(i18n.language, '/privacy'), label: t('landing.footer.legalLinks.privacy') },
              { to: legalPathForLanguage(i18n.language, '/terms'), label: t('landing.footer.legalLinks.terms') },
              { to: legalPathForLanguage(i18n.language, '/cookies'), label: t('landing.footer.legalLinks.cookies') },
            ]}
          />
        </div>

        <div className="mt-8 border-t border-ig-bg-tertiary pt-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-[12px] text-ig-text-tertiary">
              {t('landing.footer.copyright', { year: new Date().getFullYear() })}
            </p>
            <div className="flex items-center gap-3 text-ig-text-secondary">
              <a href="https://www.linkedin.com/company/interviewgator/?viewAsMember=true" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Icon name="linkedin" className="h-4 w-4 hover:text-ig-text-primary" />
              </a>
              <a href="https://www.facebook.com/Interviewgator/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Icon name="facebook" className="h-4 w-4 hover:text-ig-text-primary" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

type FooterColumnProps = {
  title: string;
  links: { to: string; label: string }[];
};

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-[11px] uppercase tracking-[0.14em] text-ig-text-tertiary">{title}</h3>
      <ul className="space-y-2 text-[14px] text-ig-text-secondary">
        {links.map((item) => (
          <li key={`${item.to}-${item.label}`}>
            {item.to.startsWith('mailto:') ? (
              <a href={item.to} className="transition-colors hover:text-ig-text-primary">
                {item.label}
              </a>
            ) : (
              <Link to={item.to} className="transition-colors hover:text-ig-text-primary">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
